{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://executable-stories.dev/schemas/raw-run.schema.json",
  "title": "RawRun",
  "description": "Raw test run data from any test framework. Feed this to the executable-stories CLI to generate HTML, Markdown, JUnit XML, and Cucumber JSON reports.",
  "type": "object",
  "$ref": "#/$defs/RawRun",
  "$defs": {
    "RawRun": {
      "type": "object",
      "description": "Top-level raw run containing all test cases and run metadata.",
      "properties": {
        "$schema": {
          "type": "string",
          "description": "Optional pointer to this schema, so editors validate the file as the adapter writes it. Ignored by the CLI."
        },
        "schemaVersion": {
          "type": "integer",
          "const": 1,
          "description": "Schema version for forward compatibility. Must be 1."
        },
        "testCases": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/RawTestCase"
          },
          "description": "All test cases from the run."
        },
        "features": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/RawFeature"
          },
          "description": "Feature declarations from story.feature(...), keyed to their source files."
        },
        "startedAtMs": {
          "type": "number",
          "minimum": 0,
          "description": "Run start time as Unix epoch milliseconds."
        },
        "finishedAtMs": {
          "type": "number",
          "minimum": 0,
          "description": "Run finish time as Unix epoch milliseconds."
        },
        "projectRoot": {
          "type": "string",
          "minLength": 1,
          "description": "Absolute path to the project root directory. Used for resolving relative attachment paths."
        },
        "packageVersion": {
          "type": "string",
          "description": "Version of the package under test."
        },
        "gitSha": {
          "type": "string",
          "description": "Git commit SHA at the time of the run."
        },
        "ci": {
          "$ref": "#/$defs/RawCIInfo"
        },
        "meta": {
          "type": "object",
          "description": "Arbitrary runner-specific metadata. Escape hatch for data not covered by the schema."
        },
        "runScope": {
          "type": "string",
          "enum": [
            "full",
            "filtered"
          ],
          "description": "How much of each source file this run covered. \"full\" means the adapter determined no name filter was applied, so the reported test cases are each file's complete set and a scenario missing from them may be retired. \"filtered\" means a name filter was applied (vitest -t, jest -t, playwright --grep), so the run saw only part of its own files. Omit when the adapter cannot tell: consumers then keep what the run did not report rather than deleting on a guess."
        },
        "coveredSourceFiles": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "description": "Source files this run executed, whether or not they produced test cases. A run's test cases only name files that produced something, so without this a file whose last scenario was deleted is indistinguishable from one that did not run. Optional."
        },
        "incompleteSourceFiles": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "description": "Source files whose scenarios could not be collected in full (a hook threw before the story was declared, a collection error, a crash). Such a file looks exactly like one whose scenarios were deleted, so it is never treated as authoritative however complete the rest of the run is."
        }
      },
      "required": [
        "schemaVersion",
        "testCases",
        "projectRoot"
      ],
      "additionalProperties": false
    },
    "RawTestCase": {
      "type": "object",
      "description": "A single test case result. Only 'status' is required \u2014 everything else is optional to support plain unit tests without BDD metadata.",
      "properties": {
        "externalId": {
          "type": "string",
          "description": "Framework's native test ID (e.g., Jest test path, Playwright test ID)."
        },
        "title": {
          "type": "string",
          "description": "Human-readable test title/name."
        },
        "titlePath": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "description": "Full title path from describe/suite blocks down to the test name."
        },
        "story": {
          "$ref": "#/$defs/StoryMeta"
        },
        "sourceFile": {
          "type": "string",
          "description": "Path to the source file containing this test (relative to projectRoot or absolute)."
        },
        "sourceLine": {
          "type": "integer",
          "minimum": 1,
          "description": "Line number in the source file (1-based)."
        },
        "status": {
          "$ref": "#/$defs/RawStatus"
        },
        "durationMs": {
          "type": "number",
          "minimum": 0,
          "description": "Test duration in milliseconds."
        },
        "error": {
          "type": "object",
          "description": "Error information if the test failed.",
          "properties": {
            "message": {
              "type": "string",
              "description": "Error message."
            },
            "stack": {
              "type": "string",
              "description": "Error stack trace."
            },
            "expected": {
              "type": "string",
              "description": "Serialized expected value, when the assertion error carries one."
            },
            "actual": {
              "type": "string",
              "description": "Serialized actual (observed) value, when the assertion error carries one."
            }
          },
          "additionalProperties": false
        },
        "stepEvents": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/RawStepEvent"
          },
          "description": "Step-level execution events from the framework (if available)."
        },
        "attachments": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/RawAttachment"
          },
          "description": "Attachments such as screenshots, logs, or other artifacts."
        },
        "meta": {
          "type": "object",
          "description": "Arbitrary framework-specific metadata. Escape hatch for data not covered by the schema."
        },
        "retry": {
          "type": "integer",
          "minimum": 0,
          "description": "Retry attempt number (0-based). 0 means first attempt."
        },
        "retries": {
          "type": "integer",
          "minimum": 0,
          "description": "Total number of retries configured for this test."
        },
        "projectName": {
          "type": "string",
          "description": "Project name (e.g., Playwright project like 'chromium', 'firefox')."
        }
      },
      "required": [
        "status"
      ],
      "additionalProperties": false
    },
    "RawStatus": {
      "type": "string",
      "enum": [
        "pass",
        "fail",
        "skip",
        "todo",
        "pending",
        "timeout",
        "interrupted",
        "unknown"
      ],
      "description": "Permissive test status from any framework. Mapped to canonical status (passed/failed/skipped/pending) during canonicalization."
    },
    "StoryMeta": {
      "type": "object",
      "description": "BDD story metadata for a test case. Contains the scenario title, steps, tags, and documentation.",
      "properties": {
        "scenario": {
          "type": "string",
          "minLength": 1,
          "description": "The scenario title (typically the test name)."
        },
        "steps": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/StoryStep"
          },
          "description": "BDD steps in this scenario. Can be empty or omitted for progressive enrichment."
        },
        "tags": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "description": "Tags for filtering and categorization (e.g., ['smoke', 'auth'])."
        },
        "tickets": {
          "type": "array",
          "items": {
            "oneOf": [
              {
                "type": "string"
              },
              {
                "type": "object",
                "properties": {
                  "id": {
                    "type": "string"
                  },
                  "url": {
                    "type": "string"
                  }
                },
                "required": [
                  "id"
                ],
                "additionalProperties": false
              }
            ]
          },
          "description": "Ticket/issue references. Each item is either a string ID or an object with id and optional url."
        },
        "covers": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "description": "Product-code paths/globs this scenario exercises (project-root-relative). Used to map code changes to at-risk scenarios."
        },
        "meta": {
          "type": "object",
          "description": "User-defined metadata for this story."
        },
        "suitePath": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "description": "Parent describe/suite names for hierarchical grouping."
        },
        "docs": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/DocEntry"
          },
          "description": "Story-level documentation entries (before any steps)."
        },
        "sourceOrder": {
          "type": "integer",
          "minimum": 0,
          "description": "Order in which the story was defined in source (for stable sorting)."
        },
        "otelSpans": {
          "type": "array",
          "items": {
            "type": "object"
          },
          "description": "OpenTelemetry spans captured for this scenario (from autotel), used to render a trace waterfall."
        }
      },
      "required": [
        "scenario"
      ],
      "additionalProperties": false
    },
    "StoryStep": {
      "type": "object",
      "description": "A single BDD step in a scenario.",
      "properties": {
        "keyword": {
          "$ref": "#/$defs/StepKeyword"
        },
        "text": {
          "type": "string",
          "description": "The step description text."
        },
        "mode": {
          "$ref": "#/$defs/StepMode"
        },
        "docs": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/DocEntry"
          },
          "description": "Rich documentation entries attached to this step."
        },
        "id": {
          "type": "string",
          "description": "Unique step identifier within the scenario (e.g., 'step-0')."
        },
        "wrapped": {
          "type": "boolean",
          "description": "Whether this step wraps a function call (Fn/Expect pattern)."
        },
        "durationMs": {
          "type": "number",
          "minimum": 0,
          "description": "Step-level duration in milliseconds (from startTimer/endTimer)."
        },
        "assertions": {
          "type": "integer",
          "minimum": 0,
          "description": "Assertions observed while this step ran. Omit when the adapter cannot observe assertions at all: absent means unknown, 0 means observed none."
        }
      },
      "required": [
        "keyword",
        "text"
      ],
      "additionalProperties": false
    },
    "StepKeyword": {
      "type": "string",
      "description": "BDD step keyword. Case-insensitive \u2014 'given' and 'Given' are both accepted. Normalized to title case during processing.",
      "pattern": "^[Gg]iven$|^[Ww]hen$|^[Tt]hen$|^[Aa]nd$|^[Bb]ut$"
    },
    "StepMode": {
      "type": "string",
      "enum": [
        "normal",
        "skip",
        "only",
        "todo",
        "fails",
        "concurrent"
      ],
      "description": "Step execution mode for documentation rendering."
    },
    "DocPhase": {
      "type": "string",
      "enum": [
        "static",
        "runtime"
      ],
      "description": "When the doc entry was added \u2014 'static' at definition time, 'runtime' during execution."
    },
    "DocEntry": {
      "description": "A documentation entry attached to a story or step. Discriminated union on the 'kind' field.",
      "oneOf": [
        {
          "type": "object",
          "description": "Free-text note.",
          "properties": {
            "kind": {
              "const": "note"
            },
            "text": {
              "type": "string"
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "text",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Tag(s) for categorization.",
          "properties": {
            "kind": {
              "const": "tag"
            },
            "names": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "names",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Key-value pair.",
          "properties": {
            "kind": {
              "const": "kv"
            },
            "label": {
              "type": "string"
            },
            "value": {},
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "label",
            "value",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Code block with optional language.",
          "properties": {
            "kind": {
              "const": "code"
            },
            "label": {
              "type": "string"
            },
            "content": {
              "type": "string"
            },
            "lang": {
              "type": "string"
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "label",
            "content",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Markdown table.",
          "properties": {
            "kind": {
              "const": "table"
            },
            "label": {
              "type": "string"
            },
            "columns": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "rows": {
              "type": "array",
              "items": {
                "type": "array",
                "items": {
                  "type": "string"
                }
              }
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "label",
            "columns",
            "rows",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Hyperlink.",
          "properties": {
            "kind": {
              "const": "link"
            },
            "label": {
              "type": "string"
            },
            "url": {
              "type": "string"
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "label",
            "url",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Titled section with markdown content.",
          "properties": {
            "kind": {
              "const": "section"
            },
            "title": {
              "type": "string"
            },
            "markdown": {
              "type": "string"
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "title",
            "markdown",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Mermaid diagram.",
          "properties": {
            "kind": {
              "const": "mermaid"
            },
            "code": {
              "type": "string"
            },
            "title": {
              "type": "string"
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "code",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Screenshot reference.",
          "properties": {
            "kind": {
              "const": "screenshot"
            },
            "path": {
              "type": "string"
            },
            "alt": {
              "type": "string"
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "path",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Video reference with optional caption and poster image.",
          "properties": {
            "kind": {
              "const": "video"
            },
            "path": {
              "type": "string"
            },
            "caption": {
              "type": "string"
            },
            "poster": {
              "type": "string"
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "path",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Embedded HTML rendered in a sandboxed iframe. Exactly one of path/url/content.",
          "properties": {
            "kind": {
              "const": "html"
            },
            "path": {
              "type": "string",
              "description": "Local HTML file path (inlined into the report by default)."
            },
            "url": {
              "type": "string",
              "description": "Remote URL rendered via iframe src."
            },
            "content": {
              "type": "string",
              "description": "Inline HTML content rendered via iframe srcdoc."
            },
            "title": {
              "type": "string"
            },
            "height": {
              "oneOf": [
                {
                  "type": "number"
                },
                {
                  "type": "string"
                }
              ],
              "description": "Iframe height: number \u2192 px, string passed through (e.g. '60vh'). Default 400px."
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "phase"
          ],
          "oneOf": [
            {
              "required": [
                "path"
              ]
            },
            {
              "required": [
                "url"
              ]
            },
            {
              "required": [
                "content"
              ]
            }
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "State snapshot captured at a step (data storyboard frame; diffed label-to-label across steps).",
          "properties": {
            "kind": {
              "const": "state"
            },
            "label": {
              "type": "string",
              "description": "Entity name ('Basket') \u2014 diff identity across steps."
            },
            "value": {
              "description": "JSON-serializable snapshot of the state at this step."
            },
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "value",
            "phase"
          ],
          "additionalProperties": false
        },
        {
          "type": "object",
          "description": "Custom documentation entry with arbitrary data.",
          "properties": {
            "kind": {
              "const": "custom"
            },
            "type": {
              "type": "string"
            },
            "data": {},
            "phase": {
              "$ref": "#/$defs/DocPhase"
            },
            "children": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/DocEntry"
              },
              "description": "Nested child doc entries for grouping."
            }
          },
          "required": [
            "kind",
            "type",
            "data",
            "phase"
          ],
          "additionalProperties": false
        }
      ]
    },
    "RawAttachment": {
      "type": "object",
      "description": "A test attachment (screenshot, log, artifact). Either path-based or inline (body); the ACL decides embed-vs-link.",
      "properties": {
        "name": {
          "type": "string",
          "description": "Human-readable name for the attachment."
        },
        "mediaType": {
          "type": "string",
          "description": "MIME type (e.g., 'image/png', 'text/plain', 'application/json')."
        },
        "path": {
          "type": "string",
          "description": "File path (relative to projectRoot or absolute)."
        },
        "body": {
          "type": "string",
          "description": "Inline content (e.g., base64-encoded image or UTF-8 text)."
        },
        "encoding": {
          "type": "string",
          "enum": [
            "BASE64",
            "IDENTITY"
          ],
          "description": "Content encoding for an inline body."
        },
        "charset": {
          "type": "string",
          "description": "Character set for IDENTITY text bodies (default utf-8)."
        },
        "fileName": {
          "type": "string",
          "description": "Actual artifact filename (distinct from the logical name)."
        },
        "byteLength": {
          "type": "integer",
          "minimum": 0,
          "description": "Size in bytes, used for embed-vs-link decisions."
        },
        "stepIndex": {
          "type": "integer",
          "minimum": 0,
          "description": "Step index this attachment belongs to (undefined = test-case level)."
        },
        "stepId": {
          "type": "string",
          "description": "Stable step ID this attachment belongs to (preferred over stepIndex)."
        }
      },
      "required": [
        "name",
        "mediaType"
      ],
      "additionalProperties": false
    },
    "RawStepEvent": {
      "type": "object",
      "description": "Step-level execution event from the framework. Optional \u2014 most frameworks don't provide step-level data.",
      "properties": {
        "index": {
          "type": "integer",
          "minimum": 0,
          "description": "Step index (0-based)."
        },
        "stepId": {
          "type": "string",
          "description": "Stable step ID when the framework provides one (correlates with StoryStep.id)."
        },
        "title": {
          "type": "string",
          "description": "Step title/description."
        },
        "status": {
          "$ref": "#/$defs/RawStatus"
        },
        "durationMs": {
          "type": "number",
          "minimum": 0,
          "description": "Step duration in milliseconds."
        },
        "errorMessage": {
          "type": "string",
          "description": "Error message if the step failed."
        }
      },
      "additionalProperties": false
    },
    "RawCIInfo": {
      "type": "object",
      "description": "CI environment information.",
      "properties": {
        "name": {
          "type": "string",
          "description": "CI provider name (e.g., 'GitHub Actions', 'Jenkins', 'CircleCI')."
        },
        "url": {
          "type": "string",
          "description": "URL to the CI build/run."
        },
        "buildNumber": {
          "type": "string",
          "description": "CI build number or run ID."
        },
        "provider": {
          "type": "string",
          "description": "Typed provider key (e.g., 'github', 'gitlab', 'circleci')."
        },
        "branch": {
          "type": "string",
          "description": "Git branch name."
        },
        "commitSha": {
          "type": "string",
          "description": "Git commit SHA."
        },
        "prNumber": {
          "type": "string",
          "description": "Pull/merge request number."
        }
      },
      "required": [
        "name"
      ],
      "additionalProperties": false
    },
    "RawFeature": {
      "type": "object",
      "description": "A feature declared in a test file: what it is for, in the words the business uses.",
      "properties": {
        "sourceFile": {
          "type": "string",
          "minLength": 1,
          "description": "Source file the declaration was made in. Declarations without one are dropped."
        },
        "title": {
          "type": "string",
          "minLength": 1,
          "description": "Heading for the feature."
        },
        "kind": {
          "type": "string",
          "enum": [
            "feature",
            "ability",
            "business-need"
          ],
          "description": "How to introduce the feature. Defaults to feature."
        },
        "narrative": {
          "type": "string",
          "description": "Markdown explaining why the feature exists and who it serves."
        },
        "tags": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "description": "Tags applied to every scenario in the file."
        },
        "glossary": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/RawGlossaryTerm"
          },
          "description": "Terms this feature defines."
        }
      },
      "required": [
        "title"
      ],
      "additionalProperties": false
    },
    "RawGlossaryTerm": {
      "type": "object",
      "description": "One glossary entry: a term the scenarios use and what it means.",
      "properties": {
        "term": {
          "type": "string",
          "minLength": 1
        },
        "definition": {
          "type": "string",
          "minLength": 1
        }
      },
      "required": [
        "term",
        "definition"
      ],
      "additionalProperties": false
    }
  }
}
